/**
 * Scopes raw ID card template CSS styles to a container selector (.id-card-scope)
 * so that:
 * 1. Template CSS does not leak out to the application or modals.
 * 2. External application CSS (Bootstrap, resets) cannot distort the ID card elements.
 */

export const ID_CARD_SCOPE_CLASS = "id-card-scope";
export const ID_CARD_SCOPE_SELECTOR = `.${ID_CARD_SCOPE_CLASS}`;

export const idCardIsolationStyles = `
/* CSS Isolation barrier for ID Card container */
${ID_CARD_SCOPE_SELECTOR} {
  isolation: isolate;
  display: inline-block;
  position: relative;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  text-align: left;
  line-height: normal;
  color: #000000;
  background-color: transparent;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

${ID_CARD_SCOPE_SELECTOR} *,
${ID_CARD_SCOPE_SELECTOR} *::before,
${ID_CARD_SCOPE_SELECTOR} *::after {
  box-sizing: border-box;
}

/* Reset leaky Bootstrap and global typography rules */
${ID_CARD_SCOPE_SELECTOR} p {
  margin: 0;
  padding: 0;
}

${ID_CARD_SCOPE_SELECTOR} h1,
${ID_CARD_SCOPE_SELECTOR} h2,
${ID_CARD_SCOPE_SELECTOR} h3,
${ID_CARD_SCOPE_SELECTOR} h4,
${ID_CARD_SCOPE_SELECTOR} h5,
${ID_CARD_SCOPE_SELECTOR} h6 {
  margin: 0;
  padding: 0;
  font-weight: normal;
}

${ID_CARD_SCOPE_SELECTOR} img {
  vertical-align: top;
  max-width: none;
  border-style: none;
}

${ID_CARD_SCOPE_SELECTOR} table {
  border-collapse: collapse;
  margin: 0;
  padding: 0;
}
`;

export function scopeCss(css, scope = ID_CARD_SCOPE_SELECTOR) {
  if (!css || typeof css !== "string") return "";

  // Remove comments
  const cleanCss = css.replace(/\/\*[\s\S]*?\*\//g, "");

  function scopeSelectors(selectorStr) {
    return selectorStr
      .split(",")
      .map(sel => sel.trim())
      .filter(sel => sel.length > 0)
      .map(sel => {
        if (/^(html|body)\b/i.test(sel)) {
          return sel.replace(/^(html|body)\b/i, scope);
        }
        if (sel === "*") {
          return `${scope}, ${scope} *`;
        }
        if (sel.startsWith("*")) {
          return `${scope} ${sel}`;
        }
        if (sel.startsWith(scope)) {
          return sel;
        }
        return `${scope} ${sel}`;
      })
      .join(", ");
  }

  let result = "";
  let i = 0;
  const len = cleanCss.length;

  while (i < len) {
    // Check for @import or @font-face
    const atRuleMatch = cleanCss.slice(i).match(/^\s*(@(import|font-face)[^{;]+[;{])/i);
    if (atRuleMatch && atRuleMatch[1].endsWith(";")) {
      result += atRuleMatch[0] + "\n";
      i += atRuleMatch[0].length;
      continue;
    }

    // Check for @media or @supports
    const mediaMatch = cleanCss.slice(i).match(/^\s*@(media|supports)[^{]*\{/i);
    if (mediaMatch) {
      const header = mediaMatch[0].trim();
      i += mediaMatch[0].length;
      let depth = 1;
      const start = i;
      while (i < len && depth > 0) {
        if (cleanCss[i] === "{") depth++;
        else if (cleanCss[i] === "}") depth--;
        i++;
      }
      const innerContent = cleanCss.slice(start, i - 1);
      const scopedInner = scopeCss(innerContent, scope);
      result += `${header} {\n${scopedInner}\n}\n\n`;
      continue;
    }

    // Check for @keyframes
    const keyframesMatch = cleanCss.slice(i).match(/^\s*@(-[a-z]+-)?keyframes[^{]*\{/i);
    if (keyframesMatch) {
      const header = keyframesMatch[0];
      i += keyframesMatch[0].length;
      let depth = 1;
      const start = i;
      while (i < len && depth > 0) {
        if (cleanCss[i] === "{") depth++;
        else if (cleanCss[i] === "}") depth--;
        i++;
      }
      result += `${header}${cleanCss.slice(start, i)}\n\n`;
      continue;
    }

    // Standard rule
    const ruleMatch = cleanCss.slice(i).match(/^\s*([^{]+)\{/);
    if (ruleMatch) {
      const rawSelectors = ruleMatch[1].trim();
      i += ruleMatch[0].length;
      const start = i;
      while (i < len && cleanCss[i] !== "}") {
        i++;
      }
      const body = cleanCss.slice(start, i).trim();
      i++;

      if (rawSelectors && !rawSelectors.startsWith("@")) {
        result += `${scopeSelectors(rawSelectors)} {\n  ${body}\n}\n\n`;
      }
      continue;
    }

    i++;
  }

  return result.trim();
}

export function getScopedTemplateCss(css, scope = ID_CARD_SCOPE_SELECTOR) {
  return `${idCardIsolationStyles}\n\n${scopeCss(css, scope)}`;
}

export default scopeCss;
